import { getApiBaseUrl } from "../utils/runtime/apiBaseUrl";
import { getResponseRequestId, isNetworkError } from "../utils/auth/authDiagnostics";

const OTP_REQUEST_RETRY_DELAY_MS = 900;

function wait(ms) {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}

function createRequestNonce() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function buildApiError(response, payload, fallbackMessage) {
    const error = new Error(payload?.message || fallbackMessage);
    error.status = response.status;
    error.code = payload?.code || null;
    error.requestId = getResponseRequestId(response, payload);
    error.details = payload?.details || null;
    error.retryAfterSeconds = payload?.details?.retryAfterSeconds || payload?.retryAfterSeconds || null;
    return error;
}

function attachRequestId(response, payload = {}) {
    return {
        ...payload,
        requestId: getResponseRequestId(response, payload),
    };
}

const api = {
    getLoginOptions: async ({ phone, email, intent, source, brokerageId, returnTo }) => {
        const response = await fetch(`${getApiBaseUrl()}/api/auth/v2/login-options`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                phone,
                email,
                intent,
                source,
                brokerageId,
                returnTo,
            }),
        });

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
            throw buildApiError(response, payload, "Unable to determine sign-in options.");
        }

        return attachRequestId(response, payload);
    },

    requestOtp: async ({ phone, email, intent, source, brokerageId, returnTo, hasExplicitIntent, channel = "sms", requestNonce }) => {
        const body = {
            phone,
            email,
            intent,
            source,
            brokerageId,
            returnTo,
            hasExplicitIntent,
            channel,
            requestNonce: requestNonce || createRequestNonce(),
        };
        const request = () => fetch(`${getApiBaseUrl()}/api/auth/v2/otp/request`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
        let response;

        try {
            response = await request();
        } catch (error) {
            if (!isNetworkError(error)) {
                throw error;
            }

            await wait(OTP_REQUEST_RETRY_DELAY_MS);
            response = await request();
        }

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
            throw buildApiError(response, payload, "Unable to request a verification code.");
        }

        return attachRequestId(response, payload);
    },

    verifyOtp: async ({ code, intent, channel, challengeId }) => {
        const response = await fetch(`${getApiBaseUrl()}/api/auth/v2/otp/verify`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                challengeId,
                code,
                intent,
                channel,
            }),
        });

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
            throw buildApiError(response, payload, "Unable to verify the one-time code.");
        }

        return attachRequestId(response, payload);
    },

    submitRecoveryRequest: async (payload) => {
        const response = await fetch(`${getApiBaseUrl()}/api/auth/v2/recovery/requests`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
            throw buildApiError(response, data, "Unable to submit the recovery request.");
        }

        return attachRequestId(response, data);
    },

    submitInvestorAccessRequest: async (payload) => {
        const response = await fetch(`${getApiBaseUrl()}/api/auth/v2/investor-access/requests`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
            throw buildApiError(response, data, "Unable to submit the iPortal access request.");
        }

        return attachRequestId(response, data);
    },
};

export default api;
